import { useEffect, useState } from "react";

import { Flex, useDisclosure } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router";

import { AssetState, DamageType } from "__graphql__/types";
import { HEIGHT as HEADER_HEIGHT } from "app/components/Header";
import { routes } from "config/routes";
import { Page } from "shared/components/Page";
import { useCustomToast } from "shared/hooks/useCustomToast";
import { useHubSlug } from "shared/hooks/useHubSlug";
import { Button } from "ui/Button/Button";
import { WarningThreeIcon } from "ui/Icons/Icons";
import { NavigationHeader } from "ui/NavigationHeader/NavigationHeader";

import { BatteryDetailsCard, BatteryStatusReasonModal, OutboundBatteryModal } from "../components";
import {
  useGetAssetByIdQuery,
  useUpdateAssetStateMutation,
  useDamageReportMutation,
} from "../queries/assets.generated";
import { DAMAGE_TYPE_OPTIONS, VALID_REPORT_STATES } from "../utils/constants";

export function BatteryReportPage() {
  const { batteryId } = useParams<{ batteryId: string }>();
  const intl = useIntl();
  const navigate = useNavigate();
  const hubSlug = useHubSlug();
  const { showToastUI } = useCustomToast();
  const {
    isOpen: isOutboundModalOpen,
    onOpen: openOutboundModal,
    onClose: closeOutboundModal,
  } = useDisclosure();
  const {
    isOpen: isBatteryStatusReasonModalOpen,
    onOpen: openBatteryStatusReasonModal,
    onClose: closeBatteryStatusReasonModal,
  } = useDisclosure();
  const [selectedOutboundReason, setSelectedOutboundReason] = useState<AssetState | null>(null);
  const [selectedNonFunctionalReason, setSelectedNonFunctionalReason] = useState<DamageType | null>(
    null,
  );

  const [updateAssetState, { loading: isUpdating }] = useUpdateAssetStateMutation();
  const [createDamageReport] = useDamageReportMutation();

  const { data, loading } = useGetAssetByIdQuery({
    variables: { input: { assetId: batteryId! } },
    skip: !batteryId,
  });

  const asset = data?.getAssetById.asset;

  const updateBatteryState = async (newState: AssetState, isFunctional: boolean) => {
    if (!asset) return;

    try {
      await updateAssetState({
        variables: {
          input: {
            assetId: asset.id,
            newState,
          },
        },
      });
      showToastUI({
        status: "success",
        description: isFunctional
          ? "pages.inventory.battery-management-page.toast.marked-functional"
          : "pages.inventory.battery-management-page.toast.marked-not-functional",
      });
    } catch {
      showToastUI({
        status: "error",
        description: "pages.inventory.battery-management-page.error.update-failed",
      });
    }
  };

  useEffect(() => {
    if (!loading && !asset) {
      navigate(routes.inventory.batteryManagement.root);
      return;
    }

    if (!loading && asset) {
      const isWrongHub = asset.hubCode !== hubSlug;
      const isInvalidState = !VALID_REPORT_STATES.includes(asset.state);

      if (isWrongHub || isInvalidState) {
        showToastUI({
          status: "error",
          description: "pages.inventory.battery-management-page.error.invalid-battery",
        });
        navigate(routes.inventory.batteryManagement.root);
      }
    }
  }, [loading, asset, navigate, hubSlug, showToastUI]);

  const handleToggleState = async (newState: AssetState) => {
    if (!asset) return;

    const isFunctional = newState === AssetState.asset_state_operational;

    if (!isFunctional) {
      openBatteryStatusReasonModal();
    } else {
      updateBatteryState(newState, isFunctional);
    }
  };

  const handleCloseBatteryStatusReasonModal = () => {
    setSelectedNonFunctionalReason(null);
    closeBatteryStatusReasonModal();
  };

  const handleCloseOutboundModal = () => {
    setSelectedOutboundReason(null);
    closeOutboundModal();
  };

  const handleBatteryStatusReason = async () => {
    if (!asset || !selectedNonFunctionalReason) return;

    try {
      await createDamageReport({
        variables: {
          input: {
            assetId: asset.id,
            type: selectedNonFunctionalReason,
            description:
              DAMAGE_TYPE_OPTIONS.find((option) => option.key === selectedNonFunctionalReason)
                ?.label || null,
          },
        },
      });
      await updateBatteryState(AssetState.asset_state_defect, true);
      handleCloseBatteryStatusReasonModal();
      showToastUI({
        status: "success",
        description: "pages.inventory.battery-management-page.toast.reported-successfully",
      });
      navigate(routes.inventory.batteryManagement.root);
    } catch {
      showToastUI({
        status: "error",
        description: "pages.inventory.battery-management-page.toast.error.report-failed",
      });
      handleCloseBatteryStatusReasonModal();
    }
  };

  const handleOutboundSubmit = async () => {
    if (!asset || !selectedOutboundReason) return;

    try {
      await updateAssetState({
        variables: {
          input: {
            assetId: asset.id,
            newState: selectedOutboundReason,
          },
        },
      });
      handleCloseOutboundModal();
      showToastUI({
        status: "success",
        description: "pages.inventory.battery-management-page.toast.reported-and-outbound",
      });
      navigate(routes.inventory.batteryManagement.root);
    } catch {
      handleCloseOutboundModal();
      showToastUI({
        status: "error",
        description: "pages.inventory.battery-management-page.error.report-failed",
      });
    }
  };

  return (
    <Page isCentered isFull isBgGrey justifyContent="flex-start">
      <NavigationHeader
        title={intl.formatMessage({ id: "pages.inventory.battery-count-report-page.title" })}
        onClickGoBack={() => navigate(routes.inventory.batteryManagement.root)}
      />
      <Flex
        position="relative"
        w="full"
        h={`calc(100vh - ${HEADER_HEIGHT})`}
        py="s200"
        direction="column"
        bg="grey.150"
      >
        <Flex px="s200" gap="s200" direction="column">
          {asset && (
            <BatteryDetailsCard
              state={asset.state}
              brand={asset.supplier?.name ?? ""}
              model={asset.model?.name ?? ""}
              id={asset.serialNumber}
              onToggle={handleToggleState}
              isToggling={isUpdating}
            />
          )}
        </Flex>
        <Flex px="s200" mt="auto" pb="s200">
          <Button
            variant="ghost"
            color="red.500"
            isFullWidth
            fontSize="16px"
            leftIcon={<WarningThreeIcon boxSize="20px" />}
            onClick={openOutboundModal}
          >
            {intl.formatMessage({
              id: "pages.inventory.battery-count-report-page.cta",
            })}
          </Button>
        </Flex>
      </Flex>

      <OutboundBatteryModal
        isOpen={isOutboundModalOpen}
        onClose={handleCloseOutboundModal}
        onClickPrimary={handleOutboundSubmit}
        onClickSecondary={handleCloseOutboundModal}
        selectedReason={selectedOutboundReason}
        setSelectedReason={setSelectedOutboundReason}
      />
      <BatteryStatusReasonModal
        isOpen={isBatteryStatusReasonModalOpen}
        onClose={handleCloseBatteryStatusReasonModal}
        onClickPrimary={handleBatteryStatusReason}
        onClickSecondary={handleCloseBatteryStatusReasonModal}
        selectedReason={selectedNonFunctionalReason}
        setSelectedReason={setSelectedNonFunctionalReason}
      />
    </Page>
  );
}
